import React from "react";

import { SUPPORTED_CARRIERS, SUPPORTED_LIFE_CARRIERS, SUPPORTED_DISABILITY_CARRIERS } from "BrokerOfRecord/constants";
import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import { Form } from "react-final-form";
import { useHistory, Link } from "react-router-dom";

import { useGetApplicationQuery, useUploadPlanDocumentsFileMutation } from "../api";
import BORPage from "../components/BORPage";
import { baseURL, contributionsURL, deductionsURL, doneURL } from "../urls";
import { H5 } from "Cobalt/Header";
import Button from "Common/Button";
import { SIFinalFormField } from "Common/forms";
import SIFinalFormMultiFileInput from "Common/forms/FileInput/SIFinalFormMultiFileInput";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import { usePartnerContext } from "Common/PartnerContext";

import makeBem from "Utils/makeBem";

const bem = makeBem("BORMainContent");

const CarrierFileUploadRow = ({ carrier, form }: { carrier: string; form: any }) => {
  const labelMap = Object.fromEntries([...SUPPORTED_CARRIERS, ...SUPPORTED_LIFE_CARRIERS, ...SUPPORTED_DISABILITY_CARRIERS]) as Record<string, string>;

  return (
    <Row className="BORCarrierRow items-center mb-4 border-b pb-2">
      <Col sm={3}>{labelMap[carrier] || carrier}</Col>
      <Col sm={6}>
        <SIFinalFormField
          name={`files.${carrier}.documents`}
          label="Upload Carrier Documents (Renewal Packets, Invoices, etc.)"
          componentProps={{ buttonColor: "grey", form }}
          component={SIFinalFormMultiFileInput}
        />
      </Col>
    </Row>
  );
};

// --- helpers ---

// Minimal fix: canonicalize carrier values using existing supported constants
const ALL_SUPPORTED = [...SUPPORTED_CARRIERS, ...SUPPORTED_LIFE_CARRIERS, ...SUPPORTED_DISABILITY_CARRIERS] as Array<[string, string]>; // [key, label]

const normalize = (s: string) =>
  String(s)
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "")
    .replace(/[^a-z0-9]/g, "");

const supportedKeySet = new Set(ALL_SUPPORTED.map(([key]) => key));
const normalizedLabelToKey = new Map(ALL_SUPPORTED.map(([key, label]) => [normalize(label), key]));

// Convert label-like carriers (e.g. "United Healthcare") to canonical keys (e.g. "unitedHealthcare")
// without adding carrier-specific mappings. Unknown carriers are returned as-is.
const canonicalizeCarrier = (carrier: any): string | null => {
  if (typeof carrier !== "string") return null;
  const raw = carrier.trim();
  if (!raw) return null;

  if (supportedKeySet.has(raw)) return raw;

  const byLabel = normalizedLabelToKey.get(normalize(raw));
  return byLabel || raw;
};

const uniqueCarriersFromPlans = (plans: any) => {
  const values = Object.values(plans || {})
    .flat()
    .flatMap((p: any) => [p?.insuranceCompany, p?.otherInsuranceCompany])
    .map(canonicalizeCarrier)
    .filter((v: any): v is string => typeof v === "string" && v.length > 0);

  return [...new Set(values)];
};

const carriersFromDocuments = (documents: any) => {
  if (!documents || typeof documents !== "object") return [];
  return Object.keys(documents)
    .map(canonicalizeCarrier)
    .filter((v: any): v is string => typeof v === "string" && v.length > 0);
};

// IMPORTANT: SIFinalFormMultiFileInput expects items with `.name`
const hydrateDocumentsForInput = (docsObj: Record<string, { fileName: string; filePath: string }>) => {
  return Object.entries(docsObj || {}).map(([displayName, d]) => ({
    name: displayName, // shows "06OrthoSBC.png" etc.
    fileName: d.fileName,
    filePath: d.filePath,
    existing: true as const,
  }));
};

// Minimal hydration fix: find the documents bucket whose key canonicalizes to the row carrier.
const getDocumentsBucketForCarrier = (documents: any, carrier: string) => {
  if (!documents || typeof documents !== "object") return {};
  if (documents[carrier]) return documents[carrier];

  const matchingKey = Object.keys(documents).find((k) => canonicalizeCarrier(k) === carrier);
  return (matchingKey && documents[matchingKey]) || {};
};

const buildCarriersAndInitialValues = (borApplication: any) => {
  const intake = borApplication?.borApplicationIntake || {};
  const plans = intake?.plans || {};
  const documents = intake?.documents || {};

  const planCarriers = uniqueCarriersFromPlans(plans);
  const documentCarriers = carriersFromDocuments(documents);

  // Show a row for every carrier we know about (either from plans or existing docs)
  const carriers = [...new Set([...planCarriers, ...documentCarriers])];

  const files: Record<string, { documents: any[] }> = {};

  carriers.forEach((carrier) => {
    const existingDocsObj = getDocumentsBucketForCarrier(documents, carrier);
    const hydrated = hydrateDocumentsForInput(existingDocsObj);
    files[carrier] = { documents: hydrated };
  });

  return { carriers, initialValues: { files } };
};

// Small helper to normalize API/RTKQ errors
const normalizeUploadError = (err: any): string => {
  return err?.data?.error || err?.error || (typeof err === "string" ? err : null) || "An unexpected error occurred while uploading. Please try again.";
};

// --- Main page ---
const FileUploadPage = () => {
  const { data: borApplication } = useGetApplicationQuery();
  const [uploadPlanDocumentsFile] = useUploadPlanDocumentsFileMutation();
  const history = useHistory();
  const { partner } = usePartnerContext();

  const { carriers, initialValues } = React.useMemo(() => {
    if (!borApplication) return { carriers: [] as string[], initialValues: { files: {} as any } };
    return buildCarriersAndInitialValues(borApplication);
  }, [borApplication]);

  const submitHandler = async (values: any) => {
    const filesByCarrier: Record<string, { documents?: any[] }> = values?.files || {};

    const uploads: Array<{ carrier: string; file: File }> = [];
    for (const [carrier, payload] of Object.entries(filesByCarrier)) {
      const docs = payload?.documents ?? [];
      for (const file of docs) {
        // only upload newly added browser Files; ignore hydrated existing docs
        if (file instanceof File) uploads.push({ carrier, file });
      }
    }

    if (uploads.length === 0) {
      history.push(partner === "netsuite" ? `${baseURL}${deductionsURL}` : `${baseURL}${doneURL}`);
      return undefined;
    }

    try {
      for (const { carrier, file } of uploads) {
        await uploadPlanDocumentsFile({ carrier, file }).unwrap(); // eslint-disable-line no-await-in-loop
      }
      history.push(partner === "netsuite" ? `${baseURL}${deductionsURL}` : `${baseURL}${doneURL}`);
    } catch (err: any) {
      return { [FORM_ERROR]: normalizeUploadError(err) };
    }
  };

  return (
    <BORPage>
      <Button component={Link} size="medium" to={`${baseURL}${contributionsURL}`} color="link" className={bem("backButton")} pl={0}>
        <span>
          <Icon icon="chevron-left" pr={3} />
          Back
        </span>
      </Button>

      <H5 className={bem("title")}>Upload Carrier Documents</H5>
      <Div className={bem("subtitle", "mb-4")}>
        In order to help expedite the process, please upload any renewal packets, invoices or plan documents you have received from carriers.
      </Div>
      <hr className={bem("divider")} />

      <Form onSubmit={submitHandler} initialValues={initialValues} mutators={{ ...arrayMutators }}>
        {({ handleSubmit, submitting, form, submitError }) => (
          <form className={bem("form")} onSubmit={handleSubmit}>
            {submitError && (
              <Div mb={3} className="text-red-600">
                {submitError}
              </Div>
            )}

            {carriers.map((carrier) => (
              <CarrierFileUploadRow key={carrier} carrier={carrier} form={form} />
            ))}

            <Row>
              <Col smOffset={8} mdOffset={9}>
                <Button fullWidth submitting={submitting} disabled={submitting} size="medium" type="submit">
                  Next
                </Button>
              </Col>
            </Row>
          </form>
        )}
      </Form>
    </BORPage>
  );
};

export default FileUploadPage;
